import { isChrome, isIOS, isDesktop } from 'react-device-detect'

import InterWebviewBridge, {
  interWbAuth,
  interWbHttp,
  interWbNavigate,
  interWbSession,
  IWbHttpData,
  IWbISafeResponse,
  IWbTac,
  IWbTic,
  IWbUserInfo,
  WbEnvironments,
} from '@interco/inter-webview-bridge'
import { HttpMethod, TResponseType } from '@services/base/types'

import {
  TWbAppInfo,
  TParamsRequestBrigde,
  TOpenPdfViewerAction,
  IWbToolbar,
  IWbAnalytics,
  TBridgeResponseError,
} from './types'

export * from './types'

export class BridgeService {
  private static interWb = InterWebviewBridge.getInstance()

  static getEnvironment(): WbEnvironments {
    return this.interWb.getEnvironment()
  }

  static isBrowser(): boolean {
    return this.interWb.getEnvironment() === WbEnvironments.BROWSER || (isChrome && isIOS)
  }

  static isDesktopBrowser(): boolean {
    return this.isBrowser() && isDesktop
  }

  static getUserInfo(): Promise<IWbUserInfo> {
    return interWbSession.getUserInfo()
  }

  static editToolbar(
    title?: string | undefined,
    show?: boolean | undefined,
    isBackNative?: boolean | undefined,
  ): Promise<IWbToolbar> {
    return interWbSession.editToolbar({ title, show, isBackNative })
  }

  static getAppInfo(): Promise<TWbAppInfo> {
    return interWbSession.getAppInfo()
  }

  static requestAnalytics(name: string, params?: Record<string, string>): Promise<IWbAnalytics> {
    if (name.startsWith('validate_')) {
      return interWbSession.requestAnalytics(name, params)
    }

    if (BridgeService.isBrowser() && typeof newrelic !== 'undefined' && newrelic) {
      newrelic.addPageAction(name, params ?? {})
      return Promise.resolve({ name, params })
    }

    return interWbSession.requestAnalytics(name, params)
  }

  static async backToNative(): Promise<void> {
    if (this.isBrowser()) {
      window.history.back()
      return
    }

    await interWbNavigate.requestGoBack().catch(() => undefined)
  }

  static async openDeepLink(url: string): Promise<void> {
    await interWbNavigate.openDeepLink(url)
  }

  static async authenticateRequestISafe(value: number): Promise<IWbISafeResponse | undefined> {
    const CATEGORY = this.interWb.getEnvironment() === 'IOS' ? '43' : ''
    const CHECK_TYPE = '5'
    const FEATURE =
      this.interWb.getEnvironment() === 'IOS'
        ? 'AUTORIZACAO_CREDITO_CONSIGNADO'
        : 'AUTORIZACAO_ANTECIPACAO_IR'

    return interWbAuth.requestISafe({
      category: CATEGORY,
      checkType: CHECK_TYPE,
      feature: FEATURE,
      value,
    })
  }

  static async request<T>({
    headers = { 'Content-Type': 'application/json' },
    pathname,
    method,
    data,
  }: TParamsRequestBrigde): Promise<TResponseType<T>> {
    const endpoint = `/mobile/${pathname}`
    const requestBody = (data ?? {}) as Record<string, unknown>
    let bridgeResponse = {} as IWbHttpData

    switch (method) {
      case HttpMethod.GET: {
        bridgeResponse = await interWbHttp.get({ endpoint, headers })
        break
      }
      case HttpMethod.POST: {
        bridgeResponse = await interWbHttp.post({
          endpoint,
          body: requestBody,
          headers,
        })
        break
      }
      case HttpMethod.PUT: {
        bridgeResponse = await interWbHttp.put({
          endpoint,
          body: requestBody,
          headers,
        })
        break
      }
      case HttpMethod.DELETE: {
        bridgeResponse = await interWbHttp.delete({ endpoint, headers })
        break
      }
      default:
        break
    }

    const isAndroid = this.interWb.getEnvironment() === WbEnvironments.ANDROID
    const isErrorStatus = bridgeResponse.httpStatus >= 400

    if (isAndroid && isErrorStatus) {
      const error = new Error(`HTTP ${bridgeResponse.httpStatus}`) as Error & TBridgeResponseError
      error.httpStatus = bridgeResponse.httpStatus
      error.response = bridgeResponse.response
      error.message = `HTTP ${bridgeResponse.httpStatus}`
      throw error
    }

    let parsedData = {} as T
    if (typeof bridgeResponse.response === 'string') {
      parsedData = JSON.parse(bridgeResponse.response || '{}')
    }

    return {
      data: parsedData,
      status: bridgeResponse.httpStatus,
      headers: bridgeResponse.headers,
    }
  }

  static openPdfViewerAction({ url, title }: TOpenPdfViewerAction) {
    if (BridgeService.isBrowser()) {
      window.open(url)
    } else {
      this.interWb.execute({
        action: 'openPdfViewer',
        metadata: { url, title },
      })
    }
  }

  static tic = (ticProps: Pick<IWbTic, 'name' | 'action'> & Partial<Pick<IWbTic, 'params'>>) => {
    if (!BridgeService.isBrowser()) {
      interWbSession.tic({
        ...ticProps,
        params: ticProps.params ?? {},
        module: 'refinanciamento_consignado',
      })
    }
  }

  static tac = (
    tacProps: Pick<IWbTac, 'name' | 'actions' | 'isError'> & Partial<Pick<IWbTac, 'params'>>,
  ) => {
    if (!BridgeService.isBrowser()) {
      interWbSession.tac({
        ...tacProps,
        params: tacProps.params ?? {},
        module: 'refinanciamento_consignado',
        isError: tacProps.isError ?? false,
      })
    }
  }
}
